// Sürücü İzinleri — ana sayfa.
// İki bölüm:
//   1. Seçili gün özeti: o tarihte izinli / müsait sürücü sayısı + izinli sürücü listesi.
//   2. Sürücü tablosu: her aktif sürücünün haftalık + yaklaşan izinleri, düzenleme butonu.
//
// İzin girişi tek bir modaldan yapılır (AvailabilityEditModal). Kaydedince her iki
// bölüm de tazelenir. Girilen izinler backend'de otomatik atama + re-optimizasyon
// havuzundan sürücüyü çıkarır (See: backend/docs/driver-scheduler.md §8.2).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import EventBusyIcon from "@mui/icons-material/EventBusy";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EditIcon from "@mui/icons-material/Edit";

import LoadingSpinner from "shared/components/UIElements/LoadingSpinner";

import useDriverSchedulerApi from "../hooks/useDriverSchedulerApi";
import WeekdayChips from "./WeekdayChips";
import AvailabilityEditModal from "./AvailabilityEditModal";
import { isoWeekday, longDate, todayYMD } from "./availabilityUtils";

import "./DriverAvailability.css";

const DriverAvailability = () => {
  const { t } = useTranslation();
  const { isLoading, listAvailability, getAvailabilityForDate, updateAvailability } = useDriverSchedulerApi();

  const [date, setDate] = useState(todayYMD());
  const [rows, setRows] = useState(null);
  const [dayInfo, setDayInfo] = useState(null);
  const [nameQuery, setNameQuery] = useState("");
  const [editRow, setEditRow] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const fetchList = useCallback(async () => {
    try {
      const result = await listAvailability();
      setRows(result?.drivers || []);
    } catch (err) {
      setErrorMessage(err?.message || t("driverScheduler.availability.fetchError"));
    }
  }, [listAvailability, t]);

  const fetchDay = useCallback(async () => {
    try {
      const result = await getAvailabilityForDate(date);
      setDayInfo(result);
    } catch (err) {
      setErrorMessage(err?.message || t("driverScheduler.availability.fetchError"));
    }
  }, [getAvailabilityForDate, date, t]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    fetchDay();
  }, [fetchDay]);

  const handleSave = useCallback(
    async (driverId, patch) => {
      const updated = await updateAvailability(driverId, patch);
      // Tablodaki satırı optimistik güncelle
      if (updated) {
        setRows((prev) => (prev || []).map((r) => (r.driver.id === driverId ? updated : r)));
      }
      setEditRow(null);
      window?.toast?.success?.(t("driverScheduler.availability.savedToast"));
      // Seçili gün panelini tazele (izin bugünü etkileyebilir)
      fetchDay();
    },
    [updateAvailability, fetchDay, t],
  );

  const filteredRows = useMemo(() => {
    const list = rows || [];
    const q = nameQuery.trim().toLocaleLowerCase("tr");
    if (!q) return list;
    return list.filter((r) => (r.driver.name || "").toLocaleLowerCase("tr").includes(q));
  }, [rows, nameQuery]);

  const selectedWeekday = isoWeekday(date);

  const reasonLabel = (reason) => t(`driverScheduler.availability.reason.${reason}`, { defaultValue: reason });

  return (
    <div className="availability">

      <div className="availability__header">
        <div>
          <h2>{t("driverScheduler.availability.title")}</h2>
          <p className="availability__intro">{t("driverScheduler.availability.intro")}</p>
          <p className="availability__note">{t("driverScheduler.availability.purposeNote")}</p>
        </div>
      </div>

      {errorMessage && <div className="availability__error">{errorMessage}</div>}

      {/* 1) Seçili gün özeti */}
      <section className="availability__day-panel">
        <div className="availability__day-header">
          <label htmlFor="availability-date">{t("driverScheduler.availability.dayLabel")}</label>
          <input
            id="availability-date"
            type="date"
            className="availability__date-input"
            value={date}
            onChange={(e) => setDate(e.target.value || todayYMD())}
          />
          {selectedWeekday && (
            <span className="availability__weekday-tag">{t(`driverScheduler.availability.weekday.${selectedWeekday}`)}</span>
          )}
        </div>

        <div className="availability__day-stats">
          <span className="availability__stat availability__stat--available">
            <EventAvailableIcon fontSize="small" />
            <strong>{dayInfo ? dayInfo.availableCount : "–"}</strong>
            {t("driverScheduler.availability.availableCount")}
          </span>
          <span className="availability__stat availability__stat--off">
            <EventBusyIcon fontSize="small" />
            <strong>{dayInfo ? dayInfo.offCount : "–"}</strong>
            {t("driverScheduler.availability.offCount")}
          </span>
          <span className="availability__stat">
            <strong>{dayInfo ? dayInfo.activeDriverCount : "–"}</strong>
            {t("driverScheduler.availability.activeCount")}
          </span>
        </div>

        <div className="availability__off-list">
          {dayInfo && dayInfo.offDrivers && dayInfo.offDrivers.length > 0 ? (
            dayInfo.offDrivers.map((o) => (
              <span key={o.driver.id} className={`availability__off-item availability__off-item--${o.reason}`}>
                <span className="availability__off-name">{o.driver.name}</span>
                <span className="availability__off-reason">{reasonLabel(o.reason)}</span>
              </span>
            ))
          ) : (
            <span className="availability__off-empty">{t("driverScheduler.availability.noneOff")}</span>
          )}
        </div>
      </section>

      {/* 2) Sürücü tablosu */}
      <section className="availability__table-section">
        <div className="availability__table-toolbar">
          <h3>{t("driverScheduler.availability.driversTitle")}</h3>
          <input
            type="text"
            className="availability__search"
            placeholder={t("driverScheduler.availability.searchPlaceholder")}
            value={nameQuery}
            onChange={(e) => setNameQuery(e.target.value)}
          />
        </div>

        {rows === null ? (
          <div className="availability__loading">
            <LoadingSpinner />
            <span>{t("driverScheduler.availability.loading")}</span>
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="availability__empty">{t("driverScheduler.availability.noDrivers")}</div>
        ) : (
          <div className="availability__table-wrap">
            <table className="availability__table">
              <thead>
                <tr>
                  <th className="availability__col-name">{t("driverScheduler.availability.table.driver")}</th>
                  <th className="availability__col-weekly">{t("driverScheduler.availability.table.weeklyOff")}</th>
                  <th className="availability__col-upcoming">{t("driverScheduler.availability.table.upcoming")}</th>
                  <th className="availability__col-action" />
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => (
                  <tr key={r.driver.id}>
                    <td className="availability__col-name">
                      <div className="availability__driver-name">{r.driver.name}</div>
                      {r.driver.phonenumber && (
                        <div className="availability__driver-phone">{r.driver.phonenumber}</div>
                      )}
                    </td>
                    <td className="availability__col-weekly">
                      <WeekdayChips selected={r.weeklyOffDays} t={t} readOnly />
                    </td>
                    <td className="availability__col-upcoming">
                      <UpcomingCell dates={r.upcomingOptOutDates} t={t} />
                    </td>
                    <td className="availability__col-action">
                      <button
                        type="button"
                        className="availability__edit-btn"
                        onClick={() => setEditRow(r)}
                        title={t("driverScheduler.availability.edit")}
                      >
                        <EditIcon fontSize="small" />
                        <span>{t("driverScheduler.availability.edit")}</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {isLoading && rows !== null && <LoadingSpinner asOverlay />}

      <AvailabilityEditModal
        show={!!editRow}
        row={editRow}
        onClose={() => setEditRow(null)}
        onSave={handleSave}
      />
    </div>
  );
};

const MAX_VISIBLE_UPCOMING = 3;

const UpcomingCell = ({ dates = [], t }) => {
  if (!dates || dates.length === 0) {
    return <span className="availability__upcoming-none">{t("driverScheduler.availability.noUpcomingLeaves")}</span>;
  }
  const visible = dates.slice(0, MAX_VISIBLE_UPCOMING);
  const extra = dates.length - visible.length;
  return (
    <span className="availability__upcoming">
      {visible.map((d) => (
        <span key={d} className="availability__upcoming-chip">
          {longDate(d)}
        </span>
      ))}
      {extra > 0 && (
        <span className="availability__upcoming-more" title={dates.map(longDate).join(", ")}>
          +{extra}
        </span>
      )}
    </span>
  );
};

export default DriverAvailability;
